/**
 * What kind of code a scan was (T07-34 スキャナーテスト, T07-35 bench). A keyboard-mode (HID)
 * scanner only types characters — it does not say which barcode it read — so the kind is
 * **guessed from the content**: JAN/EAN check digits, a slab's cert number, a URL in a QR code,
 * the shop's own label code.
 */

export type Symbology =
  "jan13" | "ean8" | "upca" | "cert" | "slab_qr" | "qr_url" | "label" | "receipt" | "other";

function checkDigitOk(digits: string): boolean {
  const body = digits.slice(0, -1);
  const check = Number.parseInt(digits.slice(-1), 10);
  let sum = 0;
  // GS1: from the right, weights 3, 1, 3, 1 …
  for (let i = 0; i < body.length; i += 1) {
    const d = Number.parseInt(body[body.length - 1 - i] ?? "0", 10);
    sum += i % 2 === 0 ? d * 3 : d;
  }
  return (10 - (sum % 10)) % 10 === check;
}

export function detectSymbology(code: string): Symbology {
  const value = code.trim();
  if (/^https?:\/\//i.test(value)) {
    return /psacard\.com|cgccards\.com|beckett\.com/i.test(value) ? "slab_qr" : "qr_url";
  }
  if (/^R\d{2,}-\d{8}-\d{4,}$/.test(value)) {
    return "receipt";
  }
  if (/^\d{13}$/.test(value) && checkDigitOk(value)) {
    return "jan13";
  }
  if (/^\d{12}$/.test(value) && checkDigitOk(value)) {
    return "upca";
  }
  if (/^\d{8}$/.test(value) && checkDigitOk(value)) {
    return "ean8";
  }
  if (/^\d{7,10}$/.test(value)) {
    return "cert";
  }
  if (/^[A-Z0-9]{1,6}(?:-[A-Z0-9]{2,12}){1,3}$/i.test(value)) {
    return "label";
  }
  return "other";
}

export interface KeyStroke {
  key: string;
  at: number;
}

export interface ScanProbe {
  code: string;
  /** The key the scanner sends after the code (Enter / Tab) — null when none arrived. */
  suffix: "Enter" | "Tab" | null;
  /** Characters the scanner puts before every code (a prefix set in the scanner), if seen. */
  prefix: string;
  /** Fastest → slowest gap between two keys (ms): a scanner types a burst < 35 ms. */
  maxGapMs: number;
  burst: boolean;
  symbology: Symbology;
}

const PREFIX_CHARS = /^[^A-Za-z0-9]+/;

/** One scan as keystrokes → what was read and how the scanner ends / starts a code. */
export function probeScan(strokes: readonly KeyStroke[], burstMs = 35): ScanProbe | null {
  const chars = strokes.filter((s) => s.key.length === 1);
  if (!chars.length) {
    return null;
  }
  const last = strokes[strokes.length - 1];
  const suffix = last?.key === "Enter" || last?.key === "Tab" ? last.key : null;
  const raw = chars.map((s) => s.key).join("");
  const prefix = PREFIX_CHARS.exec(raw)?.[0] ?? "";
  const code = raw.slice(prefix.length).trim();
  let maxGap = 0;
  for (let i = 1; i < chars.length; i += 1) {
    maxGap = Math.max(maxGap, (chars[i]?.at ?? 0) - (chars[i - 1]?.at ?? 0));
  }
  return {
    code,
    suffix,
    prefix,
    maxGapMs: Math.round(maxGap),
    burst: maxGap < burstMs,
    symbology: detectSymbology(code),
  };
}
